import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';

import {HttpClient, HttpParams} from '@angular/common/http';
import {EntityCompliancyData, OrganizationData} from '../../../types/nso-data-portal/entity-data';
import {InteractionTableData} from '../../../types/interaction/interaction-table-data';
import {
  AnnualReportingData,
  ConstitutionInformationData,
  GoodStandingData,
  GsatData,
  PolicyDocumentsData,
  ProjectCountData, SafeFromHarmData,
  SummaryNotesData
} from '../../../types/nso-data-portal/dashboard-data';
import {InteractionFilter} from '../../../types/nso-data-portal/interaction-data';
import {BaseRestService} from '../../common/BaseRestService';
import {AgeGroupsData, CensusData, CensusPeriodData} from '../../../types/nso-data-portal/census-data';
import {CustomWidgetData} from "../../../types/custom-widgets/custom-widget-data";


@Injectable()
export class OrganizationInformationRestService extends BaseRestService {

  constructor(private http: HttpClient) {
    super('ndp_organizations');
  }

  getOrganizationInformation(id: number): Observable<OrganizationData> {
    return this.http.get<OrganizationData>(this.getEndpoint([String(id)]));
  }

  getProjectCount(id: number): Observable<ProjectCountData> {
    return this.http.get<ProjectCountData>(this.getEndpoint([String(id), 'project-count']));
  }

  getSafeFromHarm(id: number) {
    return this.http.get<SafeFromHarmData>(this.getEndpoint([String(id), 'safe-from-harm']));
  }

  getGsat(id: number): Observable<GsatData> {
    return this.http.get<GsatData>(this.getEndpoint([String(id), 'gsat']));
  }

  getInteractions(id: number, filter: InteractionFilter, size?: number, page?: number): Observable<InteractionTableData> {
    let params = new HttpParams()
      .set('size', String(size))
      .set('page', String(page));

    Object.keys(filter).forEach(key => {
      if (filter[key]) {
        params = params.set(key, filter[key]);
      }
    });

    return this.http.get<InteractionTableData>(this.getEndpoint([String(id), 'interactions']), {params});
  }

  getGoodStanding(id: number): Observable<GoodStandingData> {
    return this.http.get<GoodStandingData>(this.getEndpoint([String(id), 'good-standing']));
  }

  getPolicyDocuments(id: number, size?: number, page?: number): Observable<PolicyDocumentsData> {
    const params = new HttpParams()
      .set('size', String(size))
      .set('page', String(page));

    return this.http.get<PolicyDocumentsData>(this.getEndpoint([String(id), 'policy-documents']), {params});
  }

  getConstitutionInformation(id: number): Observable<ConstitutionInformationData> {
    return this.http.get<ConstitutionInformationData>(this.getEndpoint([String(id), 'constitution-information']));
  }

  getAnnualReporting(id: number): Observable<AnnualReportingData> {
    return this.http.get<AnnualReportingData>(this.getEndpoint([String(id), 'annual-reporting']));
  }

  getSummaryNotes(id: number): Observable<SummaryNotesData> {
    return this.http.get<SummaryNotesData>(this.getEndpoint([String(id), 'summary-notes']));
  }

  changeFollowStatus(id: number): Observable<any> {
    return this.http.put<any>(this.getEndpoint([String(id), 'follow']), '');
  }

  getCustomAgeGroups(id: number): Observable<AgeGroupsData[]> {
    return this.http.get<AgeGroupsData[]>(this.getEndpoint([String(id), 'age-groups']));
  }

  saveCustomAgeGroups(id: number, customAgeGroups: AgeGroupsData[]) {
    const formData: FormData = new FormData();
    const dtoData = new Blob([JSON.stringify(customAgeGroups)], {
      type: 'application/json',
    });
    formData.append('customGroups', dtoData);
    return this.http.post(this.getEndpoint([String(id), 'age-groups']), formData);
  }

  getCensus(id: number): Observable<CensusData> {
    return this.http.get<CensusData>(this.getEndpoint([String(id), 'census']));
  }

  getCensusPeriods(id: number): Observable<CensusPeriodData[]> {
    return this.http.get<CensusPeriodData[]>(this.getEndpoint([String(id), 'census', 'censusPeriods']));
  }

  getCompliancy(id: number): Observable<EntityCompliancyData> {
    return this.http.get<EntityCompliancyData>(this.getEndpoint([String(id), 'compliancy']));
  }

  updateCensus(id: number, census: CensusData): Observable<CensusData> {
    return this.http.put<CensusData>(this.getEndpoint([String(id), 'census']), census);
  }

  updateHistoricalCensus(id: number, census: CensusData): Observable<CensusData> {
    return this.http.put<CensusData>(this.getEndpoint([String(id), 'census', 'historical']), census);
  }

  getHistoryCensusData(id: number, year: number): Observable<CensusData> {
    return this.http.get<CensusData>(
      this.getEndpoint([String(id), 'census', 'historical', String(year)])
    );
  }

  getExport(exportParams: any) {
    const params = new HttpParams()
      .set('yearFrom', exportParams.yearFrom)
      .set('yearTo', exportParams.yearTo)
      .set('level', exportParams.entityExportChoiceLevel);
    return this.http.get(this.getEndpoint(
      [String(exportParams.entityId), 'census', 'export']),
      {
        params,
        responseType: 'blob',
        observe: 'response',
      });
  }

  getNDPExport(selectedYear, orgId): any {
    const params = new HttpParams()
      .set('from', String(selectedYear - 1))
      .set('to', String(selectedYear));
    return this.http.get(
      this.getEndpoint([orgId, 'ndp-data-export']),
      {
        params,
        responseType: 'blob',
        observe: 'response',
      });
  }

  updateOrganizationContacts(orgId: number) {
    return this.http.put(this.getEndpoint([String(orgId), 'update-contacts']), {});
  }

  getWidgets(organizationId: number): Observable<CustomWidgetData[]> {
    return this.http.get<CustomWidgetData[]>(this.getEndpoint([String(organizationId), 'widgets']));
  }
}
